import "./add-new-user.scoped.scss";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import Button from "@material-ui/core/Button";
import CheckIcon from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Box,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Checkbox from "@material-ui/core/Checkbox";
import { emailRegex, stringRegex } from "../../statics/validators";
import { UserService } from "../../services/user/user.service";
import { showPopupToastMsg, showToastMsg } from "../../helpers";
import { UserRoles } from "../../types/userRoles";
const subscriptionPlans: number[] = [6, 12, 24, 36];

interface AddNewUserModalProps {
  show: boolean;
  onClose: (value: boolean) => void;
  user?: any;
  editable?: boolean;
  reactivate?: boolean;
}

const AddNewUserModal = (props: AddNewUserModalProps) => {
  const { t } = useTranslation();
  const { user, editable, reactivate, show, onClose } = props;
  const userService: UserService = new UserService();
  const [firstName, setFirstName] = useState(editable ? user.firstName : "");
  const [lastName, setLastName] = useState(editable ? user.lastName : "");
  const [email, setEmail] = useState(editable ? user.email : "");
  const [role, setRole] = useState(editable ? user.role : "");
  const [group, setGroup] = useState(editable ? user.group : "");
  const [company, setCompnay] = useState(editable ? user.company : "");
  const [plan, setPlan] = useState(
    editable ? (user.plan ? user.plan : "") : "",
  );

  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [focused, setFocused] = useState(false);
  const [defaultPasswordChecked, setDefaultPasswordChecked] = useState(false);

  const submit = async () => {
    const data: any = {
      firstName: capitalize(firstName),
      lastName: capitalize(lastName),
      email,
      role,
      group,
      company,
      plan: plan ? plan : null,
      defPassChecked: defaultPasswordChecked,
    };

    const res = editable
      ? reactivate
        ? await userService.activate(user.id, {
            role: data.role,
            plan: data.plan,
          })
        : await userService.updateUser(user.id, {
            role: data.role,
            plan: data.plan,
          })
      : await userService.addUser({ users: [data] });

    if (res && res.users) {
      if (res && res.users.length && res.users[0].message === "SUCCESS") {
        showToastMsg("success", t("USER_ADD_SUCCESSFULLY"));
      } else if (
        res &&
        res.users.length &&
        res.users[0].message === "EXISTING"
      ) {
        showPopupToastMsg("error", t("User exists already"));
      } else {
        showPopupToastMsg("error", t("System error, please try again later"));
      }
    } else if (reactivate && res && res.message === "ACTIVATED") {
      showToastMsg("success", t("user activated successfully"));
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  const onSubmit = async () => {
    if (saving) return;
    setSaving(true);
    await submit();
    setSaving(false);
    handleClose(true);
  };

  const changeRole = (value: any) => {
    setRole(value);
    if (value === UserRoles.SUPER_USER) {
      setDefaultPasswordChecked(false);
      setGroup("internal");
    } else if (value !== UserRoles.AUDITOR && editable) {
      setPlan(user.plan || plan || "");
    } else if (value === UserRoles.AUDITOR) {
      setPlan("");
    }
  };

  const handleClose = (value: any) => {
    onClose(value);
    // reset state
    setDefaultPasswordChecked(false);
    setGroup("");
    setEmailError(false);
    setEmail("");
    setLastName("");
    setFirstName("");
    setRole("");
    setPlan("");
    setFocused(false);
  };

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onFocus = (input: HTMLInputElement | null) => {
    if (input && !focused) {
      timeoutRef.current = setTimeout(() => {
        input.focus();
        setFocused(true);
      }, 1);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isAuditor = role === UserRoles.AUDITOR;
  const isPlanRequired = !isAuditor;
  const isPlanMissing = isPlanRequired && !plan;
  const roleDidNotChange = editable && user?.role === role;
  const disablePlanDropdown = isAuditor;
  const disableInAddMode = !editable && !reactivate && isPlanMissing;
  const disableInEditMode =
    editable &&
    !reactivate &&
    (roleDidNotChange ||
      (user.role === UserRoles.AUDITOR && !isAuditor && isPlanMissing) ||
      (!isAuditor && isPlanMissing));
  const disableInReactivateMode = reactivate && (!role || isPlanMissing);
  return (
    <Modal
      show={show}
      onHide={handleClose.bind(null, false)}
      centered
      dialogClassName="modal-new-user"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("invite new user")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box className="row" style={{ rowGap: "1rem" }}>
          <Box className="col-6">
            <Typography variant="subtitle2">{t("FIRST_NAME")}</Typography>
            <Box className="standard_text_input">
              <TextField
                fullWidth
                // onChange={(e: any) => setFirstName(e.target.value)}
                onChange={(e: any) => {
                  setFirstName(e.target.value);
                  e.target.value
                    ? setFirstNameError(!stringRegex.test(e.target.value))
                    : setFirstNameError(false);
                }}
                value={firstName}
                name="first name"
                variant="standard"
                placeholder={t("FIRST_NAME")}
                inputRef={onFocus}
                disabled={editable}
                inputProps={{
                  maxLength: 30,
                }}
              />
              {firstNameError && (
                <Box className="error-msg ">
                  {t("INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED")}
                </Box>
              )}
            </Box>
          </Box>
          <Box className="col-6">
            <Typography variant="subtitle2">{t("LAST_NAME")}</Typography>
            <Box className="standard_text_input">
              <TextField
                fullWidth
                placeholder={t("LAST_NAME")}
                onChange={(e: any) => {
                  setLastName(e.target.value);
                  e.target.value
                    ? setLastNameError(!stringRegex.test(e.target.value))
                    : setLastNameError(false);
                }}
                value={lastName}
                name="last name"
                variant="standard"
                disabled={editable}
                inputProps={{
                  maxLength: 30,
                }}
              />
              {lastNameError && (
                <Box className="error-msg ">
                  {t("INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED")}
                </Box>
              )}
            </Box>
          </Box>

          <Box className="col-12">
            <Typography variant="subtitle2">{t("EMAIL")}</Typography>
            <Box className="standard_text_input">
              <TextField
                fullWidth
                placeholder={t("EMAIL")}
                onChange={(e: any) => {
                  setEmail(e.target.value);
                  e.target.value
                    ? setEmailError(!emailRegex.test(e.target.value))
                    : setEmailError(false);
                }}
                value={email}
                name="email"
                variant="standard"
                type="email"
                disabled={editable}
                error={
                  emailError || firstNameError || lastNameError || companyError
                    ? true
                    : false
                }
              />
              {emailError && (
                <Box className="error-msg ">{t("invalid-email")}</Box>
              )}
            </Box>
          </Box>

          <Box className="col-6">
            <Typography variant="subtitle2">{t("ROLE")}</Typography>
            <Box className="standard_text_input">
              <FormControl className="select-input select-grey w-100">
                {!role && <InputLabel id="role">{t("role")}</InputLabel>}
                <Select
                  labelId="role"
                  name="role"
                  value={role}
                  variant="standard"
                  onChange={(e: any) => {
                    changeRole(e.target.value);
                  }}
                  IconComponent={ExpandMoreIcon}
                  disabled={editable && user.role === UserRoles.SUPER_USER}
                >
                  <MenuItem
                    value={UserRoles.SUPER_USER}
                    disabled={editable && user.role !== UserRoles.SUPER_USER}
                  >
                    {t("COORDINATOR")}
                  </MenuItem>
                  <MenuItem value={UserRoles.EDITOR}>{t("EDITOR")}</MenuItem>
                  <MenuItem value={UserRoles.AUDITOR}>{t("AUDITOR")}</MenuItem>
                  <MenuItem value={UserRoles.CONTRIBUTOR}>{t("USER")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box className="col-6">
            <Typography variant="subtitle2">{t("GROUP")}</Typography>
            <Box className="standard_text_input">
              <FormControl
                className={
                  "select-input w-100  " +
                  (role !== UserRoles.SUPER_USER ? "select-grey" : "")
                }
              >
                {!group && (
                  <InputLabel
                    id="internal/external"
                    className={
                      role === UserRoles.SUPER_USER ? "disableLabel" : ""
                    }
                  >
                    {t("internal/external")}
                  </InputLabel>
                )}
                <Select
                  labelId="internal/external"
                  name="internal/external"
                  value={group}
                  variant="standard"
                  onChange={(e: any) => setGroup(e.target.value)}
                  IconComponent={ExpandMoreIcon}
                  disabled={role === UserRoles.SUPER_USER || editable}
                >
                  <MenuItem value={"internal"}>{t("internal")}</MenuItem>
                  <MenuItem value={"external"}>{t("external")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box className="col-6">
            <Typography variant="subtitle2">{t("COMPANY")}</Typography>
            <Box className="standard_text_input">
              <TextField
                fullWidth
                placeholder={t("COMPANY")}
                onChange={(e: any) => {
                  setCompnay(e.target.value);
                  e.target.value
                    ? setCompanyError(!stringRegex.test(e.target.value))
                    : setCompanyError(false);
                }}
                value={company}
                name="compnay"
                variant="standard"
                inputProps={{
                  maxLength: 30,
                }}
                disabled={editable}
              />
              {companyError && (
                <Box className="error-msg ">
                  {t("INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED")}
                </Box>
              )}
            </Box>
          </Box>

          <Box className="col-6">
            <Typography variant="subtitle2">{t("SUBSCRIPTION")}</Typography>
            <Box className="standard_text_input">
              <FormControl
                className={
                  "select-input w-100  " +
                  (role !== UserRoles.AUDITOR ? "select-grey" : "")
                }
              >
                {!plan && (
                  <InputLabel id="subscription">{t("SUBSCRIPTION")}</InputLabel>
                )}
                <Select
                  labelId="subscription"
                  name="subscription"
                  value={plan}
                  variant="standard"
                  onChange={(e: any) => setPlan(e.target.value)}
                  IconComponent={ExpandMoreIcon}
                  disabled={disablePlanDropdown}
                >
                  {subscriptionPlans.map((item: any, index: number) => {
                    return (
                      <MenuItem value={item} key={index}>
                        {item} {t("MONTHS")}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {!editable && (
            <Box className="col-12 mx-2">
              <FormControlLabel
                className="checkbox-global"
                control={
                  <Checkbox
                    checked={defaultPasswordChecked}
                    onChange={() =>
                      setDefaultPasswordChecked(!defaultPasswordChecked)
                    }
                    name="jaChecked"
                    size="small"
                    disabled={role === UserRoles.SUPER_USER}
                  />
                }
                label={t("set default password")}
              />
            </Box>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <>
          <Button
            variant="outlined"
            color="primary"
            className="btn-secondary-theme"
            onClick={handleClose.bind(null, false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="btn-primary-theme"
            onClick={onSubmit}
            disabled={
              emailError ||
              firstNameError ||
              lastNameError ||
              companyError ||
              firstName.length === 0 ||
              lastName.length === 0 ||
              group.length === 0 ||
              email.length === 0 ||
              role.length === 0 ||
              company.length === 0 ||
              saving ||
              // mode-based conditions
              disableInAddMode ||
              disableInEditMode ||
              disableInReactivateMode
            }
          >
            {saving ? (
              <CircularProgress color="inherit" />
            ) : (
              <>
                <CheckIcon />{" "}
                {editable
                  ? reactivate
                    ? t("reactivate")
                    : t("EDIT_ROLE")
                  : t("ADD_USER")}
              </>
            )}
          </Button>
        </>
      </Modal.Footer>
    </Modal>
  );
};

export default AddNewUserModal;
